import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Download, Mail, FileText, Eye, DollarSign } from 'lucide-react';
import EmailSenderDialog from '@/components/EmailSenderDialog';
import DownloadOptionsDialog from '@/components/DownloadOptionsDialog';

const NotesAuteurList = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [noteToSend, setNoteToSend] = useState(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [noteToDownload, setNoteToDownload] = useState(null);

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    try {
      const res = await axios.get(`${API}/notes-auteur`, { withCredentials: true });
      setNotes(res.data);
    } catch (error) {
      console.error('Error loading notes d\'auteur:', error);
      toast.error('Erreur lors du chargement des notes d\'auteur');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette note d\'auteur ?')) return;
    try {
      await axios.delete(`${API}/notes-auteur/${id}`, { withCredentials: true });
      toast.success('Note d\'auteur supprimée');
      loadNotes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadPDF = (note) => {
    // Open dialog to select RIB before download
    setNoteToDownload(note);
    setDownloadDialogOpen(true);
  };

  const handleSendEmailClick = (note) => {
    setNoteToSend(note);
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async (senderEmail) => {
    try {
      await axios.post(
        `${API}/notes-auteur/${noteToSend.id}/send`,
        { sender_email: senderEmail },
        { withCredentials: true }
      );
      toast.success('Note d\'auteur envoyée par email avec succès');
      setEmailDialogOpen(false);
      setNoteToSend(null);
      loadNotes();
    } catch (error) {
      console.error('Error sending note:', error);
      toast.error('Erreur lors de l\'envoi de l\'email');
      throw error;
    }
  };

  const filteredNotes = notes.filter(note =>
    note.numero?.toLowerCase().includes(search.toLowerCase()) ||
    note.objet?.toLowerCase().includes(search.toLowerCase()) ||
    note.emetteur?.nom?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatutBadge = (statut) => {
    const variants = {
      'brouillon': 'bg-stone-200 text-stone-700',
      'envoye': 'bg-blue-100 text-blue-700'
    };
    const labels = {
      'brouillon': 'Brouillon',
      'envoye': 'Envoyé'
    };
    return <Badge className={variants[statut] || variants.brouillon}>{labels[statut] || statut}</Badge>;
  };

  const getStatutFinancierBadge = (statut) => {
    const variants = {
      'en_attente': 'bg-amber-100 text-amber-700',
      'partiellement_paye': 'bg-blue-100 text-blue-700',
      'solde': 'bg-green-100 text-green-700'
    };
    const labels = {
      'en_attente': 'En attente',
      'partiellement_paye': 'Partiel',
      'solde': 'Soldé'
    };
    return <Badge className={variants[statut] || variants.en_attente}>{labels[statut] || statut}</Badge>;
  };

  if (loading) {
    return <div className="p-6">Chargement...</div>;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-stone-800 flex items-center gap-2">
            <FileText className="h-7 w-7 text-amber-600" />
            Notes d'Auteur
          </h1>
          <p className="text-stone-600 mt-1">
            Gérez vos notes d'auteur et droits d'auteur occasionnels
          </p>
        </div>
        <Button
          onClick={() => navigate('/backoffice/notes-auteur/new')}
          className="bg-amber-600 hover:bg-amber-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle Note
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Rechercher par numéro, objet, émetteur..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Objet</TableHead>
                <TableHead>Émetteur</TableHead>
                <TableHead className="text-right">Montant TTC</TableHead>
                <TableHead className="text-right">Net à Payer</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Paiement</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredNotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-stone-500">
                    {search ? 'Aucune note trouvée' : 'Aucune note d\'auteur. Créez-en une !'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredNotes.map((note) => (
                  <TableRow key={note.id}>
                    <TableCell className="font-medium">{note.numero}</TableCell>
                    <TableCell>{note.date}</TableCell>
                    <TableCell className="max-w-xs truncate">{note.objet}</TableCell>
                    <TableCell>
                      {note.emetteur?.use_kolnoa ? (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          Kolnoa
                        </Badge>
                      ) : (
                        <span className="text-sm">{note.emetteur?.nom}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {note.total_ttc?.toFixed(2)}€
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-green-700">
                      {note.net_a_payer?.toFixed(2)}€
                    </TableCell>
                    <TableCell>{getStatutBadge(note.statut)}</TableCell>
                    <TableCell>{getStatutFinancierBadge(note.statut_financier || 'en_attente')}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          asChild
                          title="Voir"
                        >
                          <Link to={`/notes-auteur/${note.id}`} target="_blank">
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        {note.evenement_id && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            asChild
                            title="Gérer les paiements"
                            className="text-green-600 hover:text-green-700"
                          >
                            <Link to={`/backoffice/finances/${note.evenement_id}`}>
                              <DollarSign className="h-4 w-4" />
                            </Link>
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDownloadPDF(note)} 
                          title="Télécharger le PDF"
                          className="text-amber-600 hover:text-amber-700"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleSendEmailClick(note)} 
                          title="Envoyer par email"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => navigate(`/backoffice/notes-auteur/${note.id}/edit`)}
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(note.id)}
                          title="Supprimer"
                          className="text-rose-600 hover:text-rose-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Email Sender Dialog */}
      {noteToSend && (
        <EmailSenderDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          onSend={handleSendEmail}
          documentType="la note d'auteur"
          recipientEmail={noteToSend.client_email}
          documentInfo={{ numero: noteToSend.numero }}
        />
      )}

      {/* Download Options Dialog */}
      {noteToDownload && (
        <DownloadOptionsDialog
          open={downloadDialogOpen}
          onOpenChange={setDownloadDialogOpen}
          documentType="notes-auteur"
          documentId={noteToDownload.id}
          documentName={`Note d'Auteur ${noteToDownload.numero}.pdf`}
        />
      )}
    </div>
  );
};

export default NotesAuteurList;
